import { useEffect, useRef, useSyncExternalStore } from 'react';
import { Toast, useToast } from '@zillow/constellation-10';
import { onlineManager } from '@zg-rentals/react-query5';

const OFFLINE_MESSAGE = 'You’re offline. Some features may be unavailable until your connection is restored.';
// Stable key on the enqueued Toast element so we can find and dismiss this specific toast
// later (ToastProvider preserves the node's key and exposes it via `toasts`).
const OFFLINE_TOAST_KEY = 'offline-status-toast';

// Declared at module scope so their identity is stable across renders; otherwise
// `useSyncExternalStore` would re-subscribe to `onlineManager` on every render.
const subscribe = (onStoreChange: () => void) => onlineManager.subscribe(onStoreChange);
const getSnapshot = () => onlineManager.isOnline();
const getServerSnapshot = () => true; // assume online during SSR / first client render

/**
 * App-wide offline indicator, shown as a single dismissible toast.
 *
 * React Query's default `networkMode: 'online'` pauses queries while the browser is offline
 * without surfacing a per-component error, so this is the one place the app signals lost
 * connectivity. Connectivity is read from React Query's `onlineManager` — which tracks
 * `navigator.onLine` plus the `online`/`offline` window events — so the toast stays in sync
 * with when queries pause and resume.
 *
 * Renders nothing itself: it drives the shared `ToastProvider` queue, so it must be mounted
 * inside `<ToastProvider>`. The toast persists (`duration: 0`) until the user dismisses it or
 * the browser reconnects, at which point it is dismissed automatically.
 */
export default function OfflineToast() {
  const isOnline = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const { enqueueToast, dismiss, toasts } = useToast();

  // Whether the user manually closed the toast during the current offline period, so we don't
  // immediately re-enqueue it. Reset once the browser comes back online.
  const dismissedWhileOfflineRef = useRef(false);

  useEffect(() => {
    const offlineToast = toasts.find((toast) => toast?.node?.key === OFFLINE_TOAST_KEY);

    if (isOnline) {
      dismissedWhileOfflineRef.current = false;
      if (offlineToast) {
        dismiss(offlineToast.id);
      }
      return;
    }

    if (offlineToast || dismissedWhileOfflineRef.current) {
      return;
    }

    enqueueToast(
      <Toast
        key={OFFLINE_TOAST_KEY}
        tone="warning"
        onClose={() => {
          dismissedWhileOfflineRef.current = true;
        }}
      >
        {OFFLINE_MESSAGE}
      </Toast>,
      { duration: 0 }, // persist until dismissed or the browser reconnects
    );
  }, [isOnline, toasts, enqueueToast, dismiss]);

  return null;
}
